'use client';

import { useIsClient } from '@/lib/hooks/useIsClient';
import useLocalState from '@/lib/hooks/useLocalState';
import AppButton from '@/app/ui/Components/AppButton';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useToast } from '../ToastContext';
import {
  IconArrowLeft,
  IconArrowRight,
  IconClose,
} from '@/app/ui/Components/Icons';
import axios from 'axios';

function OverrideRow(props: {
  label: string;
  value: string;
  defaultValue: string;
  onClear: () => void;
}) {
  const { label, value, defaultValue, onClear } = props;
  if (!value || value === defaultValue) return null;

  return (
    <tr>
      <td>{label}</td>
      <td>{value}</td>
      <td>
        <AppButton
          className="btn-ghost"
          icon={IconClose}
          onClick={onClear}
          label={undefined}
        />
      </td>
    </tr>
  );
}

const AdminContext = createContext<{
  logRocketSessionUrl: string;
  backendUrl: string;
  graphQlUrl: string;
  impersonateUser: string;
  impersonateOrg: string;
  setLogRocketSessionUrl: (id: string) => void;
  setBackendUrl: (url: string) => void;
  setGraphQlUrl: (url: string) => void;
  setImpersonateUser: (user: string) => void;
  setImpersonateOrg: (org: string) => void;
  client: ReturnType<typeof axios.create>;
}>({
  logRocketSessionUrl: '',
  backendUrl: process.env.NEXT_PUBLIC_BE_URL || '',
  graphQlUrl: process.env.NEXT_PUBLIC_BE_GQL || '',
  impersonateUser: '',
  impersonateOrg: '',
  setLogRocketSessionUrl: () => {},
  setBackendUrl: () => {},
  setGraphQlUrl: () => {},
  setImpersonateUser: () => {},
  setImpersonateOrg: () => {},
  client: axios.create({ url: process.env.NEXT_PUBLIC_BE_URL || '' }),
});

export const AdminProvider = ({ children }: { children: ReactNode }) => {
  const [logRocketSessionId, setLogRocketSessionId] = useState('');

  const [backendUrl, setBackendUrl] = useLocalState<string>(
    'PP_BACKEND_URL',
    process.env.NEXT_PUBLIC_BE_API || '',
  );

  const [graphQlUrl, setGraphQlUrl] = useLocalState<string>(
    'PP_BACKEND_GQL',
    process.env.NEXT_PUBLIC_BE_GQL || '',
  );

  const [impersonateUser, setImpersonateUser] = useLocalState<string>(
    'PP_IMPERSONATE_USER',
    '',
  );

  const [impersonateOrg, setImpersonateOrg] = useLocalState<string>(
    'PP_IMPERSONATE_ORG',
    '',
  );

  const [impersonateLocation, setImpersonateLocation] = useLocalState<string>(
    'PP_IMPERSONATE_LOC',
    '',
  );

  const { addWarningToast } = useToast();

  const updateLogRocketSessionId = useCallback(
    (id: string) => setLogRocketSessionId(id),
    [setLogRocketSessionId],
  );

  const updateBackendUrl = useCallback(
    (url: string) =>
      url
        ? setBackendUrl(url)
        : setBackendUrl(process.env.NEXT_PUBLIC_BE_API || ''),
    [setBackendUrl],
  );

  const updateGraphQlUrl = useCallback(
    (url: string) => {
      addWarningToast({
        title: 'Must refresh browser to take effect!',
      });
      if (url) {
        setGraphQlUrl(url);
      } else {
        setGraphQlUrl(process.env.NEXT_PUBLIC_BE_GQL || '');
      }
    },
    [setGraphQlUrl, addWarningToast],
  );

  const client = useMemo(
    () =>
      axios.create({
        baseURL: backendUrl,
        withCredentials: true,
      }),
    [backendUrl],
  );

  const updateImpersonateUser = useCallback(
    (userId: string) => setImpersonateUser(userId),
    [setImpersonateUser],
  );

  const updateImpersonateOrg = useCallback(
    (orgId: string) => setImpersonateOrg(orgId),
    [setImpersonateOrg],
  );

  const isOverridden = useMemo(() => {
    return (
      graphQlUrl !== process.env.NEXT_PUBLIC_BE_GQL ||
      backendUrl !== process.env.NEXT_PUBLIC_BE_API ||
      impersonateUser !== '' ||
      impersonateOrg !== ''
    );
  }, [graphQlUrl, backendUrl, impersonateUser, impersonateOrg]);

  const isClient = useIsClient();

  if (!isClient) return null;

  return (
    <AdminContext.Provider
      value={{
        backendUrl,
        graphQlUrl,
        client,
        impersonateUser,
        impersonateOrg,
        logRocketSessionUrl: logRocketSessionId,
        setLogRocketSessionUrl: updateLogRocketSessionId,
        setBackendUrl: updateBackendUrl,
        setGraphQlUrl: updateGraphQlUrl,
        setImpersonateUser: updateImpersonateUser,
        setImpersonateOrg: updateImpersonateOrg,
      }}
    >
      {children}
      {isOverridden && (
        <div
          className={
            'flex items-center justify-center fixed bottom-0 z-20 opacity-50'
          }
          style={{
            right: impersonateLocation !== 'left' ? 0 : undefined,
            left: impersonateLocation === 'left' ? 64 : undefined,
          }}
        >
          <div className="bg-base-100 border">
            <table className="table table-sm">
              <tbody>
                <tr>
                  <td
                    colSpan={3}
                    className="cursor-pointer text-primary"
                    onClick={() =>
                      setImpersonateLocation((prior) =>
                        prior === 'left' ? 'right' : 'left',
                      )
                    }
                  >
                    <div className="flex justify-center items-center w-full">
                      {impersonateLocation === 'left' ? (
                        <IconArrowRight />
                      ) : (
                        <IconArrowLeft />
                      )}
                    </div>
                  </td>
                </tr>
                <OverrideRow
                  value={backendUrl}
                  label="BE"
                  onClear={() => updateBackendUrl('')}
                  defaultValue={process.env.NEXT_PUBLIC_BE_API || ''}
                />
                <OverrideRow
                  value={graphQlUrl}
                  label="GQL"
                  onClear={() => updateGraphQlUrl('')}
                  defaultValue={process.env.NEXT_PUBLIC_BE_GQL || ''}
                />
                <OverrideRow
                  value={impersonateUser}
                  label="User"
                  defaultValue=""
                  onClear={() => updateImpersonateUser('')}
                />
                <OverrideRow
                  value={impersonateOrg}
                  label="Org"
                  defaultValue=""
                  onClear={() => updateImpersonateOrg('')}
                />
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminContext.Provider>
  );
};

export const useBackend = () => useContext(AdminContext);
